import { Tooltip, TooltipContent, TooltipTrigger } from '@amelio/ui-library';

import { MatrixLevelCell } from './MatrixLevelCell';
import {
    MATRIX_AVERAGE_COLUMN_WIDTH,
    MATRIX_COLUMN_GAP,
    MATRIX_LABEL_COLUMN_WIDTH,
    MATRIX_ROW_MIN_HEIGHT,
} from '../reportStyles';
import { formatAverage } from '../utils/levelScale';

import type { LevelPresentation } from '../utils/levelPresentation';
import type { CompetencyMatrixCompetency, CompetencyMatrixEmployee } from '@/types/reports';

import { ProficiencyIcon } from '@/components/ProficiencyIcon/ProficiencyIcon';
import { getIconEntry } from '@/constants/proficiencyIconCatalog';
import { useAmelioTranslations } from '@/hooks';
import { getLocalizedText } from '@/utils/localization';

interface MatrixCompetencyRowProps {
    competency: CompetencyMatrixCompetency;
    employees: CompetencyMatrixEmployee[];
    scale: number;
    language: string;
    resolveLevelPresentation: (levelId: string | null) => LevelPresentation | null;
}

/** One competency row inside an expanded group: the competency name (indented under its group),
 * one level icon per employee (with a tooltip carrying the level's label), and the competency's
 * own average on the right. */
export const MatrixCompetencyRow = ({
    competency,
    employees,
    scale,
    language,
    resolveLevelPresentation,
}: MatrixCompetencyRowProps) => {
    const { t } = useAmelioTranslations();

    return (
        <div
            className={`${MATRIX_ROW_MIN_HEIGHT} ${MATRIX_COLUMN_GAP} flex w-full items-center bg-white/30`}
            data-testid={`matrix-competency-row-${competency.id}`}
        >
            <span
                className={`${MATRIX_LABEL_COLUMN_WIDTH} sticky left-0 flex shrink-0 items-center bg-white/95 py-1 pl-10`}
            >
                <span className={'text-base-500 truncate text-sm'}>
                    {getLocalizedText(competency.name)}
                </span>
            </span>

            {employees.map((employee) => {
                const employeeCompetency = employee.competencies.find(
                    (entry) => entry.competencyId === competency.id,
                );
                const presentation = resolveLevelPresentation(employeeCompetency?.levelId ?? null);
                const iconEntry = presentation?.icon ? getIconEntry(presentation.icon) : undefined;

                return (
                    <MatrixLevelCell
                        key={employee.id}
                        dataTestId={`matrix-competency-cell-${competency.id}-${employee.id}`}
                    >
                        {presentation && (iconEntry || presentation.iconUrl) ? (
                            // `ProficiencyIcon` draws an unlabelled `svg` for a catalog icon, and
                            // the tooltip below is mouse-only, so the level would reach a screen
                            // reader as nothing at all: the trigger wrapper carries the name.
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span
                                        role={'img'}
                                        aria-label={presentation.label}
                                    >
                                        <ProficiencyIcon
                                            iconEntry={iconEntry}
                                            iconUrl={presentation.iconUrl}
                                            alt={presentation.label}
                                            color={presentation.color}
                                        />
                                    </span>
                                </TooltipTrigger>

                                <TooltipContent>{presentation.label}</TooltipContent>
                            </Tooltip>
                        ) : (
                            <span
                                className={'text-base-500 text-xs'}
                                // `title` alone is a mouse-only affordance here: a plain span is
                                // a generic node, whose name assistive technology does not
                                // surface. The role gives the dash a node to hang the name on.
                                role={'img'}
                                aria-label={t('reports.competencyMatrix.matrix.notAssessed')}
                                title={t('reports.competencyMatrix.matrix.notAssessed')}
                            >
                                —
                            </span>
                        )}
                    </MatrixLevelCell>
                );
            })}

            <span
                className={`${MATRIX_AVERAGE_COLUMN_WIDTH} sticky right-0 shrink-0 bg-white/95 text-center text-sm`}
            >
                {competency.average !== null
                    ? formatAverage({ average: competency.average, scale, language })
                    : '—'}
            </span>
        </div>
    );
};
